import { Link, Stack } from '@chakra-ui/react'
import { isBefore, parseISO } from 'date-fns/esm'
import React, { FC } from 'react'
import { useLang } from '../../common/i18n/useLang'
import { Announce } from './Announce'

const FIN_SYSTEM_MAINTENANCE = parseISO('2026-08-25T13:00:00+09:00')
const FIN_SILVER_WEEK_HOLIDAY = parseISO('2026-09-24T00:00:00+09:00')
const FIN_MEXT_SCHOLARSHIP = parseISO('2026-09-01T23:59:59+09:00')

const MEXT_GUIDELINE_EN = `${import.meta.env.BASE_URL}mext_aao_guideline_en.pdf`
const MEXT_GUIDELINE_JA = `${import.meta.env.BASE_URL}mext_aao_guideline_ja.pdf`

export function showSystemMaintenance(now = new Date()) {
  return isBefore(now, FIN_SYSTEM_MAINTENANCE)
}

export function showSilverWeekHoliday(now = new Date()) {
  return isBefore(now, FIN_SILVER_WEEK_HOLIDAY)
}

export function showMextScholarship(now = new Date()) {
  return isBefore(now, FIN_MEXT_SCHOLARSHIP)
}

export function maintenanceAnnouncementCount(now = new Date()) {
  return [showSystemMaintenance(now), showSilverWeekHoliday(now), showMextScholarship(now)].filter(
    Boolean
  ).length
}

export function showMaintenance() {
  return maintenanceAnnouncementCount() > 0
}

export const Maintenance: FC<{ radius?: number; className?: string }> = ({ radius, className }) => {
  const { language } = useLang()
  const shouldShowSystemMaintenance = showSystemMaintenance()
  const shouldShowSilverWeekHoliday = showSilverWeekHoliday()
  const shouldShowMextScholarship = showMextScholarship()

  if (!shouldShowSystemMaintenance && !shouldShowSilverWeekHoliday && !shouldShowMextScholarship) {
    return null
  }

  return (
    <Stack spacing={4} className={className}>
      {shouldShowSystemMaintenance && (
        <Announce
          radius={radius}
          type={'warning'}
          title={
            language === 'ja'
              ? 'システムメンテナンスに伴うサービス一時停止のお知らせ'
              : 'AAO Online System Temporarily Unavailable Due to System Maintenance'
          }
          body={
            language === 'ja' ? (
              <div>
                この度AAOオンラインシステムは、メンテナンスのため、以下の時間帯でサービスを停止いたします。
                <br />
                <strong>【2026年8月25日（火）正午12:00 ～ 午後1:00（日本時間）】</strong>
                <br />
                上記期間中は新規の申請、および編集作業等の操作はできませんのでご注意ください。
                <br />
                ご不便をおかけいたしますが、ご理解、ご協力の程よろしくお願いいたします。
              </div>
            ) : (
              <div>
                AAO online system will be unavailable for the following time-slot due to its
                maintenance.
                <br />
                <strong>[August 25th, 2026 (Tue.) 12:00 p.m.- 1:00 p.m. (Japan Time)]</strong>
                <br />
                Please be aware that all functions on the system will become inaccessible during the
                time above.
                <br />
                We apologize for any inconvenience this may cause. Thank you for your understanding
                and cooperation.
              </div>
            )
          }
        />
      )}
      {shouldShowSilverWeekHoliday && (
        <Announce
          radius={radius}
          type={'warning'}
          title={
            language === 'ja'
              ? 'シルバーウィーク休業のお知らせ'
              : 'AAO Office Closing for Silver Week Holidays'
          }
          body={
            language === 'ja' ? (
              <div>
                シルバーウィーク休業に伴い、次の期間AAO事務室は業務をお休みいたします。
                <br />
                期間中はメールへの返信はできませんのでご了承ください。
                <br />
                <strong>9月19日（土）～ 9月23日（水）※日本時間</strong>
                <br />
                休業期間中はAAO手続きも停止しますので、早めに申請してください。
                <br />
                なお、事務室の業務時間は平日の午前9:00～午後5:00（日本時間）ですのでご注意ください。
                <br />
                ご理解とご協力の程よろしくお願いいたします。
              </div>
            ) : (
              <div>
                Due to the Silver Week holidays, the AAO office will be closed from{' '}
                <strong>September 19th to September 23rd (Japan Time).</strong>
                <br />
                We will be unable to respond to your emails/messages during this period.
                <br />
                It is recommended that applicants submit their applications in advance, as the
                process will be stopped until after the holidays.
                <br />
                Additionally, please be aware that our office hours are from 9:00 AM to 5:00 PM on
                weekdays (Japan Time).
                <br />
                Thank you for your understanding and cooperation.
              </div>
            )
          }
        />
      )}
      {shouldShowMextScholarship && (
        <Announce
          radius={radius}
          type={'warning'}
          title={
            language === 'ja'
              ? '日本政府（文部科学省）奨学金留学生　大使館推薦の第一次選考合格者の方へ'
              : 'For applicants who passed the first screening of the MEXT scholarship embassy recommendation'
          }
          body={
            language === 'ja' ? (
              <div>
                まずは
                <Link
                  href={
                    'https://www.kyoto-u.ac.jp/ja/international/students1/process/scholarship/monka#a01'
                  }
                  target={'_blank'}
                  rel={'noreferrer'}
                  color={'#319795'}
                  textDecor={'underline'}
                >
                  本学より「大学受入内諾書」の発行を希望される方への案内
                </Link>
                をご確認ください。
                <br />
                こちらを踏まえ、AAO申請をされる場合は
                <strong>
                  <u>
                    必ず
                    <Link
                      href={MEXT_GUIDELINE_JA}
                      target={'_blank'}
                      rel={'noreferrer'}
                      color={'#319795'}
                    >
                      日本政府（文部科学省）奨学金大使館推薦AAOガイドライン
                    </Link>
                    に沿って申請書を作成してください。
                  </u>
                </strong>
                <br />
                また、9月2日以降に提出された申請は受け付けられませんのでご了承ください。
              </div>
            ) : (
              <div>
                Please first check the information{' '}
                <Link
                  href={
                    'https://www.kyoto-u.ac.jp/en/education-campus/procedures/scholarships/mext#provisional_acceptance'
                  }
                  target={'_blank'}
                  rel={'noreferrer'}
                  color={'#319795'}
                  textDecor={'underline'}
                >
                  for those wishing to obtain a Letter of Provisional Acceptance from Kyoto
                  University
                </Link>
                .
                <br />
                If you choose to go through the AAO process after checking the above,{' '}
                <strong>
                  <u>
                    please make sure to carefully check the{' '}
                    <Link
                      href={MEXT_GUIDELINE_EN}
                      target={'_blank'}
                      rel={'noreferrer'}
                      color={'#319795'}
                    >
                      AAO Guideline for applicants who passed the 1st screening of the MEXT
                      Scholarship Embassy Recommendation
                    </Link>{' '}
                    and prepare your AAO application following the guideline.
                  </u>
                </strong>
                <br />
                Also, applications submitted after September 1st (Japan time) will not be accepted.
              </div>
            )
          }
        />
      )}
    </Stack>
  )
}
